/**
 * API 客户端配置
 * 包含 axios 实例和请求/响应拦截器
 */

import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { message } from 'antd';
import { API_BASE_URL } from '../utils/constants';
import { getToken, clearAuth } from '../utils/auth';

/**
 * 创建 axios 实例
 */
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 秒超时
});

/**
 * 请求拦截器 - 添加认证 token 和 Content-Type
 */
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // 如果是 FormData，不设置 Content-Type，让浏览器自动处理
    if (!config.headers['Content-Type'] && !(config.data instanceof FormData)) {
      config.headers['Content-Type'] = 'application/json';
    }
    
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

/**
 * 响应拦截器 - 统一错误处理
 */
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error: AxiosError<any>) => {
    if (axios.isCancel(error)) return Promise.reject(error);

    // 处理网络错误
    if (!error.response) {
      message.error('网络错误，请检查网络连接');
      return Promise.reject(error);
    }

    const { status, data } = error.response;

    // 处理认证错误
    if (status === 401) {
      console.error('🔐 [client.ts] 检测到 401 错误:', {
        url: error.config?.url,
        method: error.config?.method,
        data: error.response?.data
      });
      message.error('登录已过期，请重新登录');
      clearAuth();
      window.location.href = '/login';
      return Promise.reject(error);
    }

    // 处理权限错误
    if (status === 403) {
      message.error('没有权限执行此操作');
      return Promise.reject(error);
    }

    // 处理其他错误
    const errorMessage = data?.errors?.[0]?.message || data?.message || '请求失败';
    message.error(errorMessage);

    return Promise.reject(error);
  }
);

export default apiClient;

